import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { X, MapPin, Loader2, Plus, Camera, Trash2 } from "lucide-react";
import { STATUSES, LOSS_REASONS } from "../lib/helpers";
import { compressImage } from "../lib/image";
import { log } from "../lib/logger";

const BUILDING_TYPES = ["Apartment", "Independent House", "Villa", "Commercial", "Office", "Hospital", "Hotel", "Other"];
const CONSTRUCTION_STAGES = ["Planning", "Foundation", "Structure", "Brickwork", "Plastering", "Finishing", "Ready"];
const CONTACT_ROLES = ["owner", "architect", "engineer", "civil_contractor", "site_incharge", "other"];

export default function LeadFormDialog({ initial, users, onClose, onSaved, autoGeolocate = false }) {
  const { user } = useAuth();
  const isEdit = !!initial;
  const [form, setForm] = useState({
    building_name: initial?.building_name || "",
    address: initial?.address || "",
    plot_no: initial?.plot_no || "",
    street: initial?.street || "",
    locality: initial?.locality || "",
    city: initial?.city || "",
    pincode: initial?.pincode || "",
    state: initial?.state || "",
    latitude: initial?.latitude || "",
    longitude: initial?.longitude || "",
    building_type: initial?.building_type || "Apartment",
    construction_stage: initial?.construction_stage || "Foundation",
    estimated_units: initial?.estimated_units || 0,
    requirement_notes: initial?.requirement_notes || "",
    status: initial?.status || "NEW",
    assigned_to: initial?.assigned_to || "",
    loss_reason: initial?.loss_reason || "",
    loss_note: initial?.loss_note || "",
  });
  const [contacts, setContacts] = useState(initial?.contacts || []);
  const [newContact, setNewContact] = useState({ role: "owner", name: "", phone: "" });
  const [photos, setPhotos] = useState([]); // [{data, name}] — only collected during create
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchMyLocation = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation not available in this browser.");
      return;
    }
    setGeocoding(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        set("latitude", latitude);
        set("longitude", longitude);
        try {
          const r = await api.get(`/geocode/reverse?lat=${latitude}&lon=${longitude}`);
          const a = r.data;
          setForm((f) => ({
            ...f,
            address: f.address || a.address,
            plot_no: f.plot_no || a.plot_no,
            street: f.street || a.street,
            locality: f.locality || a.locality,
            city: f.city || a.city,
            pincode: f.pincode || a.pincode,
            state: f.state || a.state,
          }));
        } catch (err) {
          log.warn("[Sohom] Reverse geocode failed:", err);
        } finally {
          setGeocoding(false);
        }
      },
      (err) => { setGeocoding(false); setError("Location denied: " + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (autoGeolocate && !initial) {
      const t = setTimeout(() => { fetchMyLocation(); }, 200);
      return () => clearTimeout(t);
    }
  }, [autoGeolocate, initial]);

  const addContact = () => {
    if (!newContact.name.trim()) return;
    setContacts((arr) => [...arr, { ...newContact, name: newContact.name.trim(), phone: newContact.phone.trim() }]);
    setNewContact({ role: "owner", name: "", phone: "" });
  };

  const removeContact = (idx) => {
    setContacts((arr) => arr.filter((_, i) => i !== idx));
  };

  const onPhotoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const reads = await Promise.all(files.map(async (f) => {
      try {
        const data = await compressImage(f, { maxDim: 1600, quality: 0.75 });
        return { id: (crypto?.randomUUID?.() || `p-${Date.now()}-${Math.random().toString(36).slice(2)}`), data, name: f.name };
      } catch (err) {
        log.warn("[Sohom] Image compression failed for", f.name, err);
        return { error: err?.message || "Photo failed", name: f.name };
      }
    }));
    const good = reads.filter((r) => r && !r.error);
    const bad = reads.filter((r) => r && r.error);
    setPhotos((arr) => [...arr, ...good]);
    if (bad.length) {
      // Surface a single, actionable alert per batch so the user isn't left wondering.
      alert(bad.map((r) => `Skipped ${r.name}: ${r.error}`).join("\n"));
    }
  };

  const removePhoto = (idx) => {
    setPhotos((arr) => arr.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setError("");
    // Front-end enforce: LOST requires reason + note before hitting the API.
    if (form.status === "LOST") {
      if (!form.loss_reason) {
        setSaving(false);
        setError("Please pick a reason for the loss.");
        return;
      }
      if (!form.loss_note.trim()) {
        setSaving(false);
        setError("Please add a detailed note explaining the loss.");
        return;
      }
    }
    try {
      const payload = {
        ...form,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        estimated_units: Number(form.estimated_units) || 0,
        assigned_to: form.assigned_to || null,
        contacts,
      };
      if (!payload.building_name) payload.building_name = "Unnamed";
      // Only send loss fields when relevant.
      if (payload.status !== "LOST") {
        delete payload.loss_reason;
        delete payload.loss_note;
      } else {
        payload.loss_note = payload.loss_note.trim();
      }
      if (isEdit) {
        await api.patch(`/leads/${initial.id}`, payload);
      } else {
        // Include photos in create payload
        payload.photos = photos.map((p) => ({ data: p.data }));
        await api.post("/leads", payload);
      }
      onSaved();
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const canAssign = user?.role !== "field_executive";
  const assignableUsers = users.filter((u) => u.role !== "admin");

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="lead-form-dialog">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{isEdit ? `Edit ${initial.lead_no}` : "New Lead"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="close-dialog"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
          <button type="button" onClick={fetchMyLocation} className="sohom-btn-secondary" data-testid="use-location-btn" disabled={geocoding}>
            {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            {geocoding ? "Fetching…" : "Use my location & auto-fill"}
          </button>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Building Name (optional)" value={form.building_name} onChange={(v) => set("building_name", v)} testid="form-building-name" />
            <Field label="Plot No." value={form.plot_no} onChange={(v) => set("plot_no", v)} testid="form-plot-no" />
            <Field label="Street" value={form.street} onChange={(v) => set("street", v)} testid="form-street" />
            <Field label="Locality / Area" value={form.locality} onChange={(v) => set("locality", v)} testid="form-locality" />
            <Field label="City" value={form.city} onChange={(v) => set("city", v)} testid="form-city" />
            <Field label="Pincode" value={form.pincode} onChange={(v) => set("pincode", v)} testid="form-pincode" />
            <Field label="State" value={form.state} onChange={(v) => set("state", v)} testid="form-state" />
            <div className="hidden sm:block"></div>
            <Field label="Address (auto-filled)" value={form.address} onChange={(v) => set("address", v)} testid="form-address" full />
            <Field label="Latitude" value={form.latitude} onChange={(v) => set("latitude", v)} testid="form-lat" type="number" />
            <Field label="Longitude" value={form.longitude} onChange={(v) => set("longitude", v)} testid="form-lng" type="number" />
            <SelectField label="Building Type" value={form.building_type} onChange={(v) => set("building_type", v)} options={BUILDING_TYPES} testid="form-building-type" />
            <SelectField label="Construction Stage" value={form.construction_stage} onChange={(v) => set("construction_stage", v)} options={CONSTRUCTION_STAGES} testid="form-stage" />
            <Field label="Estimated Units" value={form.estimated_units} onChange={(v) => set("estimated_units", v)} type="number" testid="form-units" />
            <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES} testid="form-status" />
            {form.status === "LOST" && (
              <>
                <div className="sm:col-span-2 -mt-1">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Loss Reason <span className="text-red-500">*</span></label>
                  <select
                    className="sohom-input mt-1"
                    value={form.loss_reason}
                    onChange={(e) => set("loss_reason", e.target.value)}
                    data-testid="form-loss-reason"
                  >
                    <option value="">— Select a reason —</option>
                    {LOSS_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Loss Note <span className="text-red-500">*</span></label>
                  <textarea
                    className="sohom-input mt-1"
                    rows={3}
                    placeholder="What happened? Competitor / price gap / timeline mismatch, etc."
                    value={form.loss_note}
                    onChange={(e) => set("loss_note", e.target.value)}
                    data-testid="form-loss-note"
                  />
                </div>
              </>
            )}
            {canAssign && (
              <SelectField
                label="Assign To"
                value={form.assigned_to}
                onChange={(v) => set("assigned_to", v)}
                options={[{ value: "", label: "— Unassigned —" }, ...assignableUsers.map((u) => ({ value: u.id, label: `${u.full_name} (${u.role})` }))]}
                testid="form-assigned"
              />
            )}
            <Field label="Requirement Notes" value={form.requirement_notes} onChange={(v) => set("requirement_notes", v)} full testarea testid="form-notes" />
          </div>

          {/* Contacts */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Contacts ({contacts.length})</h3>
            </div>
            <div className="space-y-2 mb-3">
              {contacts.length === 0 ? (
                <div className="text-sm text-slate-400 italic">No contacts added yet.</div>
              ) : contacts.map((c, i) => (
                <div key={`${c.role}-${c.name}-${c.phone}-${i}`} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2 bg-slate-50" data-testid={`form-contact-${i}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 text-sm truncate">{c.name} <span className="text-xs text-slate-500 ml-1">({c.role.replace("_"," ")})</span></div>
                    {c.phone && <div className="text-xs text-slate-500 truncate">{c.phone}</div>}
                  </div>
                  <button type="button" onClick={() => removeContact(i)} className="p-1 hover:bg-white rounded text-slate-500 ml-2 shrink-0" data-testid={`form-remove-contact-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[7rem_1fr_9rem_auto] gap-2 items-end">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Role</label>
                <select className="sohom-input mt-1" value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} data-testid="form-contact-role">
                  {CONTACT_ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Name</label>
                <input className="sohom-input mt-1" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Contact name" data-testid="form-contact-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Phone</label>
                <input className="sohom-input mt-1" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="+91…" data-testid="form-contact-phone" />
              </div>
              <button type="button" onClick={addContact} className="sohom-btn-primary justify-center" data-testid="form-add-contact-btn">
                <Plus className="w-4 h-4" /> <span className="sm:hidden">Add contact</span><span className="hidden sm:inline">Add</span>
              </button>
            </div>
          </div>

          {/* Photos — only for new-lead create (edit uses dedicated endpoint on detail page) */}
          {!isEdit && (
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Photos ({photos.length})</h3>
                <label className="sohom-btn-secondary text-xs cursor-pointer" data-testid="form-add-photo-btn">
                  <Camera className="w-3.5 h-3.5" /> Add image
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/*" multiple hidden onChange={onPhotoFiles} />
                </label>
              </div>
              {photos.length === 0 ? (
                <div className="text-sm text-slate-400 italic">No photos added yet.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((p, i) => (
                    <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group" data-testid={`form-photo-thumb-${i}`}>
                      <img src={p.data} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity sm:opacity-100"
                        data-testid={`form-remove-photo-${i}`}
                        aria-label="Remove photo"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
        </form>
        <div className="px-5 sm:px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="sohom-btn-secondary" data-testid="cancel-btn">Cancel</button>
          <button onClick={submit} className="sohom-btn-primary" disabled={saving} data-testid="save-lead-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isEdit ? "Save changes" : "Create lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", full, testarea, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      {testarea ? (
        <textarea className="sohom-input mt-1" rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input type={type} className="sohom-input mt-1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <select className="sohom-input mt-1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {opts.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
